import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthenticatedFetch } from '../../../hooks/useAuth';
import type { PositionScenarioTransition, PositionScenarioTransitionPort } from '../commands/PositionScenarioTransitionCommand';
import type { PositionStaffingOperationNotice } from './usePositionStaffingCommands';
import { PositionCommandSettlementError, isPositionCommandConflict, settlePositionCommand } from './positionCommandSettlement';
import { enqueueSuccessionTransition, registerOrgPlanningCommandBarrier } from './orgPlanningCommandSequencer';
import { successionOptimisticContext } from '../stores/successionOptimisticSlateStore';
import { useOrgPlanningScenarioStore } from '../stores/orgPlanningScenarioStore';

export function usePositionScenarioCommands(input: {
  folderId: string;
  diagramId?: string;
  onNotice: (notice: PositionStaffingOperationNotice) => void;
  onScenarioChangeSet?: (changeSetId: string | null) => void;
}) {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const [isPending, setIsPending] = React.useState(false);

  const post = React.useCallback(async (
    changeSetId: string,
    action: PositionScenarioTransition | 'restore' | 'revert',
    signal?: AbortSignal,
  ) => {
    const response = await settlePositionCommand(authenticatedFetch(
      action === 'apply'
        ? `/api/folders/${input.folderId}/diagrams/${input.diagramId ?? 'test'}/atlas/organization-changes/${changeSetId}/apply`
        : `/api/folders/${input.folderId}/diagrams/${input.diagramId ?? 'test'}/atlas/organization-plans/${changeSetId}/${action}`,
      { method: 'POST', signal },
    ), 'request_timeout');
    const result = await response.json().catch(() => null) as { id?: string; changeSetId?: string; graphVersion?: number; error?: string } | null;
    if (!response.ok) {
      throw new PositionCommandSettlementError(
        'http_error',
        response.status,
        result?.error ?? `Scenario ${action} failed.`,
      );
    }
    const resultChangeSetId = result?.changeSetId ?? result?.id;
    if (!resultChangeSetId || !Number.isFinite(result?.graphVersion)) {
      throw new PositionCommandSettlementError('http_error', 502, 'Malformed scenario response.');
    }
    return { id: resultChangeSetId, graphVersion: result!.graphVersion as number };
  }, [authenticatedFetch, input.diagramId, input.folderId]);

  const reconcile = React.useCallback((context: string, message: string, graphVersion: number) => {
    input.onNotice({ state: 'idle', message });
    registerOrgPlanningCommandBarrier({
      context,
      queryClient,
      folderId: input.folderId,
      graphVersion,
      onDelayed: () => input.onNotice({
        state: 'indeterminate',
        message: 'Committed; verifying latest organization…',
      }),
    });
  }, [input, queryClient]);

  const guarded = React.useCallback(async (operation: () => Promise<void>) => {
    setIsPending(true);
    input.onNotice({ state: 'pending', message: 'Updating scenario…' });
    try {
      await operation();
    } catch (error) {
      input.onNotice({
        state: isPositionCommandConflict(error) ? 'conflict' : 'failed',
        message: scenarioTransitionErrorMessage(error),
      });
      throw error;
    } finally {
      setIsPending(false);
    }
  }, [input]);

  const createServerPort = React.useCallback((initialId: string, action: PositionScenarioTransition): PositionScenarioTransitionPort => {
    const diagramId = input.diagramId ?? 'test';
    const context = successionOptimisticContext(input.folderId, diagramId);
    let currentId = initialId;
    let inverseId: string | null = null;
    let replacementDiscarded = false;
    return {
      execute: () => enqueueSuccessionTransition(context, (signal) => guarded(async () => {
        currentId = useOrgPlanningScenarioStore.getState().selectedByDiagram[diagramId]?.headChangeSetId
          ?? currentId;
        // Apply itself reauthorizes, validates graph/source/policy versions and
        // simulates post-state. A mandatory review GET here duplicated that
        // work and added a full network waterfall without strengthening the
        // server boundary.
        const result = await post(currentId, action, signal);
        currentId = result.id;
        input.onScenarioChangeSet?.(action === 'rebase' ? currentId : null);
        reconcile(context, action === 'apply' ? 'Scenario applied.' : action === 'discard' ? 'Scenario discarded.' : 'Scenario rebased.', result.graphVersion);
      }), queryClient, {
        lifecycleKey: action,
        allowFailedPrerequisites: action === 'discard',
      }),
      undo: () => enqueueSuccessionTransition(context, (signal) => guarded(async () => {
        if (action === 'apply') {
          if (!inverseId) {
            const result = await post(currentId, 'revert', signal);
            inverseId = result.id;
            reconcile(context, `Scenario ${action} undone.`, result.graphVersion);
          }
        } else if (action === 'discard') {
          const result = await post(initialId, 'restore', signal);
          currentId = result.id;
          reconcile(context, `Scenario ${action} undone.`, result.graphVersion);
        } else {
          if (!replacementDiscarded) {
            await post(currentId, 'discard', signal);
            replacementDiscarded = true;
          }
          const result = await post(initialId, 'restore', signal);
          currentId = result.id;
          replacementDiscarded = false;
          reconcile(context, `Scenario ${action} undone.`, result.graphVersion);
        }
        input.onScenarioChangeSet?.(action === 'apply' ? null : currentId);
      }), queryClient, { lifecycleKey: `undo:${action}` }),
      redo: () => enqueueSuccessionTransition(context, (signal) => guarded(async () => {
        if (action === 'apply') {
          if (!inverseId) throw new Error('Scenario apply has not been undone.');
          const result = await post(inverseId, 'revert', signal);
          currentId = result.id;
          inverseId = null;
          reconcile(context, `Scenario ${action} redone.`, result.graphVersion);
        } else {
          const result = await post(currentId, action, signal);
          currentId = result.id;
          reconcile(context, `Scenario ${action} redone.`, result.graphVersion);
        }
        input.onScenarioChangeSet?.(action === 'apply' || action === 'discard' ? null : currentId);
      }), queryClient, { lifecycleKey: `redo:${action}` }),
    };
  }, [guarded, input, post, queryClient, reconcile]);

  return { createServerPort, isPending };
}

function scenarioTransitionErrorMessage(error: unknown): string {
  if (!(error instanceof PositionCommandSettlementError)) {
    return error instanceof Error ? error.message : 'Scenario transition failed.';
  }
  if (isPositionCommandConflict(error)) {
    return 'The organization changed. Review and rebase the scenario before trying again.';
  }
  if (error.serverCode === 'invalid_post_state') {
    return 'This scenario no longer produces a valid organization state. Review its conflicts before trying again.';
  }
  if (error.serverCode === 'invalid_change_set' || error.serverCode === 'change_set_not_found') {
    return 'The scenario state changed. Refresh the chart before trying again.';
  }
  if (error.code === 'request_timeout' || error.code === 'refresh_timeout') {
    return 'The scenario update did not settle. Refresh the chart before trying again.';
  }
  return 'The scenario transition could not be completed.';
}
